/**
 * ProviderProductivityReport — Feb 28, 2026
 *
 * Printable / exportable per-user productivity report. Hits the new
 * `/api/me/stats/report` endpoint with arbitrary date ranges, renders an
 * OER-ready document, and exposes three exports:
 *   - Print           (window.print, optimized print CSS)
 *   - Save as PDF     (Print dialog → Save as PDF — same surface)
 *   - Download CSV    (every row in the summary + tops + daily breakdown)
 *
 * Surfaced from the Header user menu so the provider can pull a report
 * any time without leaving the workflow.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Printer, FileDown, Calendar, RefreshCw, FileText, TrendingUp,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '../ui/button';

const API = window.__WR_API_BASE__;

// Quick-pick range presets — every Army OER reviewer asks for one of these.
const PRESETS = [
  { id: '7d',    label: 'Last 7 days',     days: 7 },
  { id: '30d',   label: 'Last 30 days',    days: 30 },
  { id: '90d',   label: 'Last 90 days',    days: 90 },
  { id: 'qtr',   label: 'This Quarter',    quarter: true },
  { id: 'ytd',   label: 'Year to Date',    ytd: true },
  { id: '12mo',  label: 'Last 12 months',  days: 365 },
];

const toISODate = (d) => d.toISOString().slice(0, 10);

const computePreset = (presetId) => {
  const today = new Date();
  if (presetId === 'qtr') {
    const q = Math.floor(today.getMonth() / 3);
    const start = new Date(today.getFullYear(), q * 3, 1);
    return { from: toISODate(start), to: toISODate(today) };
  }
  if (presetId === 'ytd') {
    const start = new Date(today.getFullYear(), 0, 1);
    return { from: toISODate(start), to: toISODate(today) };
  }
  const preset = PRESETS.find((p) => p.id === presetId);
  const days = preset?.days || 30;
  const start = new Date(today);
  start.setDate(start.getDate() - (days - 1));
  return { from: toISODate(start), to: toISODate(today) };
};

const REASON_LABELS = {
  other: 'Other / Not specified',
  sick_call: 'Sick Call',
  msk: 'MSK (Musculoskeletal)',
  medication_refill: 'Medication Refill',
  med_efficacy_eval: 'Med Efficacy Eval',
  annual_exam: 'Annual Wellness Exam',
  annual_lab_review: 'Annual + Lab Review',
  annual_health_promo: 'Annual + Health Promo',
  lab_review: 'Lab Review',
  imaging_review: 'Imaging Review',
  consult_follow_up: 'Consult Follow-Up',
  chronic_care: 'Chronic Care Mgmt',
  profile_eval: 'Profile Eval',
  bp_log_review: 'BP Log Review',
  adhd_med_mgmt: 'ADHD Med Mgmt',
  weight_med_mgmt: 'Weight Med Mgmt',
  mental_health: 'Mental Health',
  deployment_health: 'Deployment Health',
  procedure: 'Procedure',
};

// CSV builder — single string, safe quoting per RFC 4180.
const csvEscape = (v) => {
  const s = (v === null || v === undefined) ? '' : String(v);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};
const buildCsv = (report) => {
  const lines = [];
  const provider = report.provider || {};
  const range = report.range || {};
  const summary = report.summary || {};
  lines.push('# WriteRight PCM+ Provider Productivity Report');
  lines.push(`# Generated: ${report.generated_at || ''}`);
  lines.push(`# Provider: ${csvEscape(provider.name)} (${csvEscape(provider.rank)} ${csvEscape(provider.mtf)})`);
  lines.push(`# Date Range: ${range.from} to ${range.to} (${range.days_in_range} days)`);
  lines.push('');
  lines.push('# SUMMARY');
  lines.push('metric,value');
  Object.entries(summary).forEach(([k, v]) => lines.push(`${csvEscape(k)},${csvEscape(v)}`));
  lines.push('');
  lines.push('# TOP REASONS FOR VISIT');
  lines.push('reason,count');
  (report.top_reasons || []).forEach((r) =>
    lines.push(`${csvEscape(REASON_LABELS[r.reason] || r.reason)},${csvEscape(r.count)}`),
  );
  lines.push('');
  lines.push('# TOP ICD-10 CODES');
  lines.push('code,count');
  (report.top_icd10 || []).forEach((r) =>
    lines.push(`${csvEscape(r.code)},${csvEscape(r.count)}`),
  );
  if ((report.daily_breakdown || []).length > 0) {
    lines.push('');
    lines.push('# DAILY BREAKDOWN');
    lines.push('day,sessions,clinical_notes,imaging_orders,referrals,admin_letters');
    report.daily_breakdown.forEach((d) =>
      lines.push([
        d.day, d.sessions, d.clinical_notes, d.imaging_orders, d.referrals, d.admin_letters,
      ].map(csvEscape).join(',')),
    );
  }
  return lines.join('\r\n');
};

const downloadFile = (filename, content, mime = 'text/csv;charset=utf-8;') => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
};

const StatTile = ({ label, value, sub, accent = 'sky' }) => {
  const accents = {
    sky:     'from-sky-500/20 to-sky-500/5 border-sky-500/40 text-sky-200',
    emerald: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/40 text-emerald-200',
    amber:   'from-amber-500/20 to-amber-500/5 border-amber-500/40 text-amber-200',
    purple:  'from-purple-500/20 to-purple-500/5 border-purple-500/40 text-purple-200',
    rose:    'from-rose-500/20 to-rose-500/5 border-rose-500/40 text-rose-200',
  }[accent] || 'from-secondary/40 to-secondary/10 border-border';
  return (
    <div className={`rounded-lg border bg-gradient-to-br p-3 ${accents}`} data-testid={`report-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">{label}</div>
      <div className="text-2xl font-bold leading-tight mt-1">{value}</div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
};

export const ProviderProductivityReport = ({ onClose }) => {
  const [activePreset, setActivePreset] = useState('30d');
  const [{ from, to }, setRange] = useState(() => computePreset('30d'));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async (fromDate, toDate) => {
    if (!API) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/api/me/stats/report`, {
        params: { from: fromDate, to: toDate, include_daily: true },
        withCredentials: true,
      });
      setReport(r.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to load report';
      setError(msg);
      toast.error(`Report failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReport(from, to); }, [fetchReport, from, to]);

  const handlePreset = (id) => {
    setActivePreset(id);
    const r = computePreset(id);
    setRange(r);
  };

  const handleCustomChange = (which, value) => {
    setActivePreset('custom');
    setRange((cur) => ({ ...cur, [which]: value }));
  };

  const handleCSV = () => {
    if (!report) return;
    const fname = `WriteRight_Productivity_${report.range.from}_to_${report.range.to}.csv`;
    downloadFile(fname, buildCsv(report));
    toast.success('CSV downloaded');
  };

  const handlePrint = () => { window.print(); };

  const provider = report?.provider || {};
  const summary = report?.summary || {};
  const reasonsList = useMemo(() => {
    if (!report?.top_reasons) return [];
    const total = report.top_reasons.reduce((sum, r) => sum + r.count, 0) || 1;
    return report.top_reasons.map((r) => ({
      ...r,
      label: REASON_LABELS[r.reason] || r.reason,
      pct: Math.round((r.count / total) * 100),
    }));
  }, [report]);

  const dailyMax = useMemo(() => {
    if (!report?.daily_breakdown?.length) return 0;
    return Math.max(...report.daily_breakdown.map((d) => d.sessions));
  }, [report]);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 print:relative print:bg-white print:p-0 print:inset-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="provider-productivity-report-overlay"
    >
      <div
        className="bg-card border border-border rounded-lg shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-y-auto print:shadow-none print:max-h-none print:overflow-visible print:border-0 print:w-full print:max-w-full print-area"
        data-testid="provider-productivity-report"
      >
        {/* Header / Controls — hidden in print */}
        <div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border px-4 py-3 print:hidden">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-amber-400" />
                <h2 className="text-base font-bold tracking-tight">Provider Productivity Report</h2>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                OER-ready summary for any date range — Print, save as PDF, or download CSV.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-secondary rounded"
              data-testid="report-close-btn"
              title="Close (Esc)"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {/* Preset chips */}
            {PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => handlePreset(p.id)}
                className={`px-2 py-0.5 rounded-full border text-[10px] font-medium transition-all whitespace-nowrap ${
                  activePreset === p.id
                    ? 'bg-amber-500/20 border-amber-400/60 text-amber-200 ring-1 ring-amber-400/40'
                    : 'bg-secondary/40 border-border text-muted-foreground hover:bg-amber-500/10 hover:border-amber-500/40 hover:text-amber-300'
                }`}
                data-testid={`report-preset-${p.id}`}
              >
                {p.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-1.5">
              <Calendar className="w-3 h-3 text-muted-foreground" />
              <input
                type="date"
                value={from}
                max={to}
                onChange={(e) => handleCustomChange('from', e.target.value)}
                className="bg-secondary/40 border border-border rounded px-1.5 py-0.5 text-[11px] focus:outline-none focus:border-amber-500/60"
                data-testid="report-date-from"
              />
              <span className="text-[10px] text-muted-foreground">to</span>
              <input
                type="date"
                value={to}
                min={from}
                onChange={(e) => handleCustomChange('to', e.target.value)}
                className="bg-secondary/40 border border-border rounded px-1.5 py-0.5 text-[11px] focus:outline-none focus:border-amber-500/60"
                data-testid="report-date-to"
              />
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-[11px]"
                onClick={() => fetchReport(from, to)}
                disabled={loading}
                data-testid="report-refresh-btn"
                title="Refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              onClick={handlePrint}
              className="h-7 px-3 text-[11px] bg-sky-600 hover:bg-sky-500 text-white gap-1.5"
              data-testid="report-print-btn"
              disabled={!report}
            >
              <Printer className="w-3.5 h-3.5" />
              Print / Save as PDF
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCSV}
              className="h-7 px-3 text-[11px] gap-1.5"
              data-testid="report-csv-btn"
              disabled={!report}
            >
              <FileDown className="w-3.5 h-3.5" />
              Download CSV
            </Button>
          </div>
        </div>

        {/* PRINT-AREA BODY */}
        <div className="p-4 print:p-6">
          {/* Print-only header */}
          <div className="hidden print:block mb-6 border-b-2 border-black pb-3">
            <h1 className="text-2xl font-bold">WriteRight PCM+ — Provider Productivity Report</h1>
            <div className="text-sm mt-1">
              <span className="font-semibold">{provider.name || 'Provider'}</span>
              {provider.rank && <span> · {provider.rank}</span>}
              {provider.mtf && <span> · {provider.mtf}</span>}
            </div>
            <div className="text-xs mt-1">
              Range: <strong>{report?.range?.from}</strong> – <strong>{report?.range?.to}</strong>
              {' '}({report?.range?.days_in_range} days)
              {report?.generated_at && (
                <span className="ml-3 opacity-70">
                  Generated {new Date(report.generated_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>

          {loading && !report && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
              Loading report…
            </div>
          )}
          {error && (
            <div className="p-3 rounded border border-rose-500/40 bg-rose-500/10 text-rose-200 text-xs flex items-start gap-2" data-testid="report-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>{error}</div>
            </div>
          )}

          {report && !loading && (
            <>
              {/* Identity strip (screen only) */}
              <div className="mb-4 p-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.04] print:hidden">
                <div className="text-[10px] uppercase font-bold text-amber-300/80 tracking-wider">Provider</div>
                <div className="text-sm font-semibold mt-0.5">
                  {provider.name || 'Provider'}
                  {provider.rank && <span className="text-muted-foreground"> · {provider.rank}</span>}
                  {provider.mtf && <span className="text-muted-foreground"> · {provider.mtf}</span>}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Range: <strong>{report.range.from}</strong> – <strong>{report.range.to}</strong> ({report.range.days_in_range} days)
                </div>
              </div>

              {/* Summary tiles */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-5 print:grid-cols-4">
                <StatTile label="Total Sessions" value={summary.total_sessions} accent="sky" />
                <StatTile label="Clinical Notes" value={summary.clinical_notes} accent="emerald" />
                <StatTile label="Imaging Orders" value={summary.imaging_orders} accent="purple" />
                <StatTile label="Referrals" value={summary.referrals} accent="amber" />
                <StatTile label="Admin Letters" value={summary.admin_letters} accent="rose" />
                <StatTile label="Limitations" value={summary.limitations} accent="sky" />
                <StatTile label="Profiles" value={summary.profiles} accent="emerald" />
                <StatTile
                  label="Time Saved"
                  value={`${summary.time_saved_hours || 0}h`}
                  sub={`${summary.time_saved_minutes || 0} min`}
                  accent="amber"
                />
              </div>

              {/* Submission funnel */}
              <div className="mb-5">
                <h3 className="text-[11px] uppercase font-bold tracking-wider text-muted-foreground mb-2">
                  Submission Funnel
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  <StatTile label="Drafts" value={summary.draft_count || 0} accent="rose" sub="Not generated yet" />
                  <StatTile label="Generated · Pending Genesis" value={summary.unsubmitted_count || 0} accent="amber" sub="Awaiting submission" />
                  <StatTile label="Submitted to Genesis" value={summary.submitted_count || 0} accent="emerald" sub="Complete" />
                </div>
              </div>

              {/* Top Reasons + Top ICD-10 — side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 print:grid-cols-2">
                <div className="rounded-lg border border-border bg-secondary/20 p-3" data-testid="report-top-reasons">
                  <h3 className="text-[11px] uppercase font-bold tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                    <FileText className="w-3 h-3" />
                    Top Reasons for Visit
                  </h3>
                  {reasonsList.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground italic">No data in range</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {reasonsList.map((r) => (
                        <li key={r.reason} className="text-[11px]">
                          <div className="flex justify-between mb-0.5">
                            <span>{r.label}</span>
                            <span className="font-semibold tabular-nums">{r.count} <span className="text-muted-foreground">({r.pct}%)</span></span>
                          </div>
                          <div className="h-1 bg-secondary/60 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-amber-500/70 to-amber-400/70 print:bg-amber-500"
                              style={{ width: `${r.pct}%` }}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="rounded-lg border border-border bg-secondary/20 p-3" data-testid="report-top-icd10">
                  <h3 className="text-[11px] uppercase font-bold tracking-wider text-muted-foreground mb-2">
                    Top ICD-10 Codes
                  </h3>
                  {(report.top_icd10 || []).length === 0 ? (
                    <div className="text-[11px] text-muted-foreground italic">No data in range</div>
                  ) : (
                    <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
                      {report.top_icd10.map((c) => (
                        <li key={c.code} className="text-[11px] flex justify-between">
                          <span className="font-mono">{c.code}</span>
                          <span className="font-semibold tabular-nums text-muted-foreground">×{c.count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Daily breakdown sparkline + table */}
              {report.daily_breakdown && report.daily_breakdown.length > 0 && (
                <div className="rounded-lg border border-border bg-secondary/20 p-3 mb-5" data-testid="report-daily-breakdown">
                  <h3 className="text-[11px] uppercase font-bold tracking-wider text-muted-foreground mb-2">
                    Daily Breakdown
                  </h3>
                  {/* Sparkline */}
                  <div className="flex items-end gap-0.5 h-12 mb-2">
                    {report.daily_breakdown.map((d) => {
                      const h = dailyMax > 0 ? Math.max(2, Math.round((d.sessions / dailyMax) * 100)) : 0;
                      return (
                        <div
                          key={d.day}
                          className="flex-1 bg-amber-500/40 hover:bg-amber-400/70 rounded-sm transition-colors print:bg-amber-500"
                          style={{ height: `${h}%`, minWidth: '2px' }}
                          title={`${d.day}: ${d.sessions} session${d.sessions === 1 ? '' : 's'}`}
                        />
                      );
                    })}
                  </div>
                  {/* Compact table — only visible in print + on hover-expand in screen */}
                  <details className="text-[10px] print:open">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                      Show daily table
                    </summary>
                    <table className="w-full mt-2 text-[10px] tabular-nums">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b border-border">
                          <th className="py-1 pr-2">Day</th>
                          <th className="py-1 pr-2 text-right">Sessions</th>
                          <th className="py-1 pr-2 text-right">Notes</th>
                          <th className="py-1 pr-2 text-right">Imaging</th>
                          <th className="py-1 pr-2 text-right">Referrals</th>
                          <th className="py-1 text-right">Admin Letters</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.daily_breakdown.map((d) => (
                          <tr key={d.day} className="border-b border-border/30">
                            <td className="py-0.5 pr-2 font-mono">{d.day}</td>
                            <td className="py-0.5 pr-2 text-right">{d.sessions}</td>
                            <td className="py-0.5 pr-2 text-right">{d.clinical_notes}</td>
                            <td className="py-0.5 pr-2 text-right">{d.imaging_orders}</td>
                            <td className="py-0.5 pr-2 text-right">{d.referrals}</td>
                            <td className="py-0.5 text-right">{d.admin_letters}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                </div>
              )}

              {/* Signature line (print only) */}
              <div className="hidden print:block mt-8 pt-4 border-t border-black/40">
                <div className="text-[10px] uppercase font-bold tracking-wider mb-1">Provider Signature</div>
                <div className="border-b border-black w-64 h-8"></div>
                <div className="text-[10px] mt-2">{provider.signature_block || ''}</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderProductivityReport;
